// Help-page document model. Pure functions only — the build-time glob import
// of the markdown lives in docsBundle.ts so this stays unit-testable.
//
// The corpus is `docs/user-guide/*.md` (all roles) and `docs/runbooks/*.md`
// (admin only), bundled into the SPA at build time (#150/#151). Because the
// markdown ships with the build, the Help page updates on every deploy — no
// runtime sync service.

export type DocGroup = 'guide' | 'runbook';

export interface HelpDoc {
  /** Stable slug used as the section anchor / nav key, e.g. `01-getting-started`. */
  id: string;
  /** Display title (frontmatter `title:` if present, else the first H1, else the id). */
  title: string;
  group: DocGroup;
  /** Sort key within the group. User-guide files are numbered (`01-`, `02-`); the
   *  numeric prefix orders them and unnumbered files (glossary, faq, README) sort last. */
  order: number;
  /** Markdown body with the YAML frontmatter block stripped. */
  body: string;
}

interface ParsedFrontmatter {
  title?: string;
  body: string;
}

// Minimal YAML-frontmatter split: we only ever read `title:`. A leading `---`
// fence delimits the block; everything after the closing `---` is the body.
export function parseFrontmatter(raw: string): ParsedFrontmatter {
  const normalized = raw.replace(/^\uFEFF/, '');
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(normalized);
  if (!match) return { body: normalized };
  const block = match[1];
  const body = normalized.slice(match[0].length);
  const titleLine = /^title:\s*(.+?)\s*$/m.exec(block);
  const title = titleLine ? titleLine[1].replace(/^['"]|['"]$/g, '') : undefined;
  return { title, body };
}

function firstHeading(body: string): string | undefined {
  const m = /^#\s+(.+?)\s*$/m.exec(body);
  return m ? m[1].trim() : undefined;
}

// Derive the slug + numeric order from a file path like
// `../../docs/user-guide/01-getting-started.md`.
function slugAndOrder(path: string): { id: string; order: number } {
  const base = path.split('/').pop() ?? path;
  const id = base.replace(/\.md$/i, '');
  const numMatch = /^(\d+)/.exec(id);
  // Unnumbered files (glossary, faq, README) sort after numbered ones.
  const order = numMatch ? Number(numMatch[1]) : 1000;
  return { id, order };
}

function titleCaseSlug(id: string): string {
  return id
    .replace(/^\d+-/, '')
    .split('-')
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

// Turn a { path: rawMarkdown } map (one group) into sorted HelpDoc[].
export function buildDocs(files: Record<string, string>, group: DocGroup): HelpDoc[] {
  const docs = Object.entries(files).map(([path, raw]) => {
    const { title: fmTitle, body } = parseFrontmatter(raw);
    const { id, order } = slugAndOrder(path);
    const title = fmTitle ?? firstHeading(body) ?? titleCaseSlug(id);
    return { id, title, group, order, body };
  });
  return docs.sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}
